import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CheckCircle2, Clock, ExternalLink, RefreshCw, RotateCcw, Search, ShieldCheck,
  UserCheck, UserX, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { filtereKonten, formatiereZeitpunkt } from '../../lib/zugaenge';

function Anfangsbuchstaben({ name, email }) {
  const quelle = (name || email || '?').trim();
  const teile = quelle.split(/[\s@.]+/).filter(Boolean).slice(0, 2);
  const kuerzel = teile.map((t) => t[0]).join('').toUpperCase() || '?';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 34, height: 34, borderRadius: '50%', background: '#E2E8F0',
      color: '#475569', fontSize: 12, fontWeight: 800, flexShrink: 0,
    }}>
      {kuerzel}
    </span>
  );
}

function Schalter({ an, onClick, disabled, titel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={titel}
      aria-pressed={an}
      style={{
        position: 'relative', width: 38, height: 21, borderRadius: 999, border: 'none',
        background: an ? '#15803D' : '#CBD5E1', cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1, transition: 'background 120ms', flexShrink: 0, padding: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: an ? 20 : 3, width: 15, height: 15,
        borderRadius: '50%', background: '#fff', transition: 'left 120ms',
      }} />
    </button>
  );
}

/**
 * Zugangsverwaltung fuer alle EN:POWER Tools.
 *
 * Oben stehen die Konten, die auf eine Freigabe warten - das ist der Teil, der
 * taeglich Aufmerksamkeit braucht. Darunter die bereits freigegebenen Konten mit
 * den Schaltern fuer die Werkzeuge, die eine eigene Freigabe verlangen.
 */
export default function ZugangsanfragenPage({ apiBase, session }) {
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState('');
  const [aktiv, setAktiv] = useState('');
  const [suche, setSuche] = useState('');

  const authHeaders = useMemo(() => (
    session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
  ), [session]);

  const laden = useCallback(async (mitHinweis = false) => {
    setLaedt(true);
    setFehler('');
    try {
      const antwort = await axios.get(`${apiBase}/automation/zugangsanfragen`, { headers: authHeaders });
      setDaten(antwort.data);
      if (mitHinweis) toast.success('Aktualisiert.');
    } catch (error) {
      setFehler(error?.response?.data?.detail || error.message || 'Zugänge konnten nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => { laden(false); }, [laden]);

  const schalte = async ({ userId, werkzeug, frei, marke }) => {
    setAktiv(marke);
    try {
      await axios.post(`${apiBase}/automation/zugangsanfragen`, {
        userId, werkzeug, freigegeben: frei,
      }, { headers: authHeaders });
      await laden(false);
      toast.success(frei ? 'Freigeschaltet.' : 'Zugang entzogen.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Änderung fehlgeschlagen.');
    } finally {
      setAktiv('');
    }
  };

  /** Ablehnen und Zurücknehmen laufen über eigene Aktionen, nicht über den Freigabe-Schalter. */
  const entscheide = async ({ userId, aktion, marke, erfolgstext }) => {
    setAktiv(marke);
    try {
      await axios.post(`${apiBase}/automation/zugangsanfragen`, { userId, aktion }, { headers: authHeaders });
      await laden(false);
      toast.success(erfolgstext);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Änderung fehlgeschlagen.');
    } finally {
      setAktiv('');
    }
  };

  const werkzeuge = daten?.werkzeuge || [];
  const wartend = daten?.wartend || [];
  const abgelehnt = daten?.abgelehnt || [];
  const freigegeben = filtereKonten(daten?.freigegeben || [], suche);

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 22, fontWeight: 800, color: '#0F172A', margin: 0 }}>
            <ShieldCheck size={22} color="#E07000" />
            Zugänge freischalten
          </h1>
          <p style={{ color: '#64748B', fontSize: 13, margin: '6px 0 0', maxWidth: 640, lineHeight: 1.5 }}>
            Neue Konten sind gesperrt, bis sie hier freigegeben werden. Ausgenommen sind
            nur Kolleginnen und Kollegen, die schon in Bitrix stehen – die kommen
            direkt hinein.
          </p>
        </div>
        <button
          type="button"
          onClick={() => laden(true)}
          disabled={laedt}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff',
            border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 14px',
            fontSize: 13, fontWeight: 600, color: '#334155',
            cursor: laedt ? 'default' : 'pointer', opacity: laedt ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} />
          {laedt ? 'Lädt…' : 'Aktualisieren'}
        </button>
      </div>

      {fehler && (
        <div style={{
          background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C',
          borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 20, lineHeight: 1.5,
        }}>
          {fehler}
        </div>
      )}

      <section style={{ marginBottom: 30 }}>
        <h2 style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase',
          letterSpacing: '0.06em', margin: '0 0 10px',
        }}>
          <Clock size={13} />
          Wartet auf Freigabe
          {wartend.length > 0 && (
            <span style={{
              background: '#FEF3C7', color: '#92400E', borderRadius: 999,
              padding: '1px 8px', fontSize: 11,
            }}>
              {wartend.length}
            </span>
          )}
        </h2>

        {wartend.length === 0 ? (
          <div style={{
            background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12,
            padding: '18px 20px', fontSize: 13, color: '#64748B',
            display: 'flex', alignItems: 'center', gap: 9,
          }}>
            <CheckCircle2 size={15} color="#15803D" />
            {laedt ? 'Wird geladen…' : 'Keine offenen Anfragen.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {wartend.map((konto) => (
              <article
                key={konto.id}
                style={{
                  background: '#fff', border: '1px solid #E2E8F0', borderLeft: '4px solid #F59E0B',
                  borderRadius: 12, padding: '13px 16px',
                  display: 'flex', alignItems: 'center', gap: 13, flexWrap: 'wrap',
                }}
              >
                <Anfangsbuchstaben name={konto.name} email={konto.email} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>
                    {konto.name || konto.email}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748B' }}>
                    {konto.name ? `${konto.email} · ` : ''}
                    angelegt {formatiereZeitpunkt(konto.angelegtAm)}
                  </div>
                </div>

                {konto.inBitrix && (
                  <span style={{
                    background: '#DCFCE7', color: '#15803D', fontSize: 11, fontWeight: 700,
                    padding: '3px 9px', borderRadius: 999,
                  }}>
                    in Bitrix bekannt
                  </span>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const wen = konto.name || konto.email;
                      // Rueckfrage, weil die Anfrage danach aus der Warteliste verschwindet.
                      if (!window.confirm(`Zugangsanfrage von ${wen} ablehnen?\n\nDas Konto bleibt gesperrt. Sie können die Ablehnung jederzeit zurücknehmen.`)) return;
                      entscheide({
                        userId: konto.id,
                        aktion: 'ablehnen',
                        marke: `ab-${konto.id}`,
                        erfolgstext: 'Anfrage abgelehnt.',
                      });
                    }}
                    disabled={aktiv === `ab-${konto.id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      background: '#fff', color: '#B91C1C', border: '1px solid #FCA5A5',
                      borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700,
                      cursor: aktiv === `ab-${konto.id}` ? 'default' : 'pointer',
                      opacity: aktiv === `ab-${konto.id}` ? 0.6 : 1,
                    }}
                  >
                    <UserX size={14} />
                    Ablehnen
                  </button>

                  <button
                    type="button"
                    onClick={() => schalte({ userId: konto.id, frei: true, marke: `frei-${konto.id}` })}
                    disabled={aktiv === `frei-${konto.id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      background: '#E07000', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '8px 15px', fontSize: 13, fontWeight: 700,
                      cursor: aktiv === `frei-${konto.id}` ? 'default' : 'pointer',
                      opacity: aktiv === `frei-${konto.id}` ? 0.6 : 1,
                    }}
                  >
                    <UserCheck size={14} />
                    Freischalten
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {abgelehnt.length > 0 && (
        <section style={{ marginBottom: 30 }}>
          <h2 style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase',
            letterSpacing: '0.06em', margin: '0 0 10px',
          }}>
            <UserX size={13} />
            Abgelehnt
            <span style={{
              background: '#FEE2E2', color: '#B91C1C', borderRadius: 999,
              padding: '1px 8px', fontSize: 11,
            }}>
              {abgelehnt.length}
            </span>
          </h2>

          <div style={{ display: 'grid', gap: 8 }}>
            {abgelehnt.map((konto) => (
              <article
                key={konto.id}
                style={{
                  background: '#fff', border: '1px solid #E2E8F0', borderLeft: '4px solid #DC2626',
                  borderRadius: 12, padding: '13px 16px',
                  display: 'flex', alignItems: 'center', gap: 13, flexWrap: 'wrap',
                }}
              >
                <Anfangsbuchstaben name={konto.name} email={konto.email} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>
                    {konto.name || konto.email}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748B' }}>
                    {konto.name ? `${konto.email} · ` : ''}
                    abgelehnt {formatiereZeitpunkt(konto.abgelehntAm)}
                    {konto.abgelehntVon ? ` von ${konto.abgelehntVon}` : ''}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => entscheide({
                      userId: konto.id,
                      aktion: 'ablehnung-aufheben',
                      marke: `zurueck-${konto.id}`,
                      erfolgstext: 'Anfrage wartet wieder auf Freigabe.',
                    })}
                    disabled={aktiv === `zurueck-${konto.id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      background: '#fff', color: '#475569', border: '1px solid #CBD5E1',
                      borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700,
                      cursor: aktiv === `zurueck-${konto.id}` ? 'default' : 'pointer',
                      opacity: aktiv === `zurueck-${konto.id}` ? 0.6 : 1,
                    }}
                  >
                    <RotateCcw size={14} />
                    Zurück in die Warteliste
                  </button>

                  <button
                    type="button"
                    onClick={() => schalte({ userId: konto.id, frei: true, marke: `frei-${konto.id}` })}
                    disabled={aktiv === `frei-${konto.id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      background: '#E07000', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '8px 15px', fontSize: 13, fontWeight: 700,
                      cursor: aktiv === `frei-${konto.id}` ? 'default' : 'pointer',
                      opacity: aktiv === `frei-${konto.id}` ? 0.6 : 1,
                    }}
                  >
                    <UserCheck size={14} />
                    Doch freischalten
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap', margin: '0 0 10px',
        }}>
          <h2 style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase',
            letterSpacing: '0.06em', margin: 0,
          }}>
            <Users size={13} />
            Freigegebene Konten
          </h2>
          <div style={{ position: 'relative' }}>
            <Search size={13} color="#94A3B8" style={{ position: 'absolute', left: 10, top: 9 }} />
            <input
              type="search"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Name oder E-Mail"
              style={{
                border: '1px solid #E2E8F0', borderRadius: 8, padding: '7px 12px 7px 30px',
                fontSize: 13, color: '#0F172A', background: '#fff', width: 220, fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        {(daten?.fremdverwaltet || []).length > 0 && (
          <div style={{
            background: '#F1F5F9', color: '#475569', fontSize: 12, lineHeight: 1.5,
            padding: '9px 13px', borderRadius: 8, marginBottom: 10,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <ExternalLink size={14} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>
              {daten.fremdverwaltet.map((w) => (
                <span key={w.titel}>
                  <strong>{w.titel}</strong> {w.hinweis} –{' '}
                  <a
                    href={w.adresse}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#E07000', fontWeight: 600 }}
                  >
                    dort verwalten
                  </a>
                </span>
              ))}
            </span>
          </div>
        )}

        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 620 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Konto
                </th>
                {werkzeuge.map((w) => (
                  <th key={w.id} style={{ textAlign: 'center', padding: '10px 12px', fontSize: 11, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                    {w.titel}
                  </th>
                ))}
                <th style={{ textAlign: 'center', padding: '10px 16px', fontSize: 11, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Grundzugang
                </th>
              </tr>
            </thead>
            <tbody>
              {freigegeben.length === 0 && (
                <tr>
                  <td colSpan={werkzeuge.length + 2} style={{ padding: '16px', color: '#94A3B8', textAlign: 'center' }}>
                    {laedt ? 'Wird geladen…' : 'Keine Treffer.'}
                  </td>
                </tr>
              )}
              {freigegeben.map((konto, index) => (
                <tr key={konto.id} style={{ borderTop: index === 0 ? 'none' : '1px solid #F1F5F9' }}>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Anfangsbuchstaben name={konto.name} email={konto.email} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 7 }}>
                          {konto.name || konto.email}
                          {konto.istAdmin && (
                            <span style={{ background: '#E0E7FF', color: '#3730A3', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999 }}>
                              Admin
                            </span>
                          )}
                        </div>
                        {konto.name && <div style={{ color: '#94A3B8', fontSize: 12 }}>{konto.email}</div>}
                      </div>
                    </div>
                  </td>

                  {werkzeuge.map((w) => (
                    <td key={w.id} style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <Schalter
                        an={konto.werkzeuge?.[w.id] === true}
                        disabled={aktiv === `${w.id}-${konto.id}`}
                        titel={`${w.titel} für ${konto.email}`}
                        onClick={() => schalte({
                          userId: konto.id,
                          werkzeug: w.id,
                          frei: !(konto.werkzeuge?.[w.id] === true),
                          marke: `${w.id}-${konto.id}`,
                        })}
                      />
                    </td>
                  ))}

                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <Schalter
                      an
                      disabled={aktiv === `frei-${konto.id}` || konto.istAdmin}
                      titel={konto.istAdmin
                        ? 'Administratoren werden hier nicht gesperrt.'
                        : `Grundzugang für ${konto.email} entziehen`}
                      onClick={() => schalte({ userId: konto.id, frei: false, marke: `frei-${konto.id}` })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ color: '#94A3B8', fontSize: 11, margin: '10px 0 0', lineHeight: 1.5 }}>
          NAP-Tool und Kundenportal verlangen eine eigene Freigabe – der Grundzugang
          allein genügt dort nicht. Beim Freischalten des Grundzugangs verschickt das
          Anmelde-Projekt automatisch eine Willkommensmail.
        </p>
      </section>
    </div>
  );
}
